import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  Community, 
  Incident, 
  IncidentComment,
  Payment, 
  Poll, 
  Reservation,
  Message,
  Forum,
  CommonArea,
  IncidentStatus
} from '../types';
import { SupportRequest } from '@/types/support';
import type { Alert } from '@/types/dashboard';
import { updateCommunityHousingConfig as updateCommunityHousingConfigApi } from '@/services/communityService';
import {
  createIncidentRecord,
  addIncidentComment as addIncidentCommentRecord,
  removeIncidentComment as removeIncidentCommentRecord,
  forceDeleteIncidentRecord,
  deleteIncidentRecord,
  updateIncidentStatusRecord,
  updateIncidentRecord,
} from '@/services/incidentService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { normalizeReservation } from '@/utils/reservations/normalize';
import {
  createCommunityPaymentRecord,
  deleteCommunityPaymentRecord,
  updateCommunityPaymentRecord,
} from '@/services/paymentService';
import type { PaymentSearchAction } from '@/types/paymentSearch';
import { getErrorMessage } from '@/utils/errors';
import type { DataContextType, DataLoadStates, DataLoadStatus, PaymentActionFilters } from './dataContextTypes';
import {
  fetchCommunitiesForUser,
  fetchCommonAreasForUser,
  fetchIncidentsForUser,
  fetchMissingPrimaryCommunity,
  fetchPaymentsForCommunities,
} from './dataContextLoaders';
import {
  hasUserVotedInPoll,
  selectAreaReservations,
  selectCommonArea,
  selectCommunity,
  selectCommunityForums,
  selectCommunityIncidents,
  selectCommunityMembers,
  selectCommunityMessages,
  selectCommunityPayments,
  selectCommunityPolls,
  selectConversation,
  selectDayReservations,
  selectForumMessages,
  selectIncident,
  selectPayment,
  selectPoll,
  selectReservation,
  selectUserPayments,
  selectUserPrivateMessages,
  selectUserReservations,
} from './dataContextSelectors';
import {
  addPollVote,
  applyCommunityHousingConfig,
  cancelReservationById,
  buildLevyPayments,
  buildPaymentsCsv,
  buildSupportRequestAlert,
  canDeletePayment,
  canUserReserveArea,
  closePollById,
  createForumRecord,
  createMessageRecord,
  createPollRecord,
  createReservationRecord,
  filterOverduePayments,
  filterPaymentsForReceipts,
  findReservationConflict,
  hasStringArrayField,
  isCompletedPayment,
  isCreateLevyActionPayload,
  normalizePaymentAttachments,
  prependUniqueById,
  removeById,
  replaceById,
  resolvePaidDate,
  toPaymentUpdateInput,
  removeCommonAreaByScope,
  upsertCommonArea,
  validateIncidentDeletion,
} from './dataContextMutations';

const DataContext = createContext<DataContextType | undefined>(undefined);

interface CommunityDataContextType {
  communities: Community[];
  reloadCommunities: () => Promise<Community[]>;
}

const CommunityDataContext = createContext<CommunityDataContextType | undefined>(undefined);

// Helpers
const generateId = (): string => Math.random().toString(36).substring(2, 11);
const getCurrentDate = (): string => new Date().toISOString();
const createInitialDataLoadStates = (status: DataLoadStatus = 'idle'): DataLoadStates => ({
  communities: status,
  payments: status,
  incidents: status,
  commonAreas: status,
});
const pad2 = (n: number) => String(n).padStart(2, '0');
const sortCommunitiesByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
const normalizeTime = (t: string): string => {
  if (!t) return '00:00:00';
  const parts = t.split(':').map(p => p.trim());
  const h = parseInt(parts[0] ?? '0', 10) || 0;
  const m = parseInt(parts[1] ?? '0', 10) || 0;
  const s = parseInt(parts[2] ?? '0', 10) || 0;
  const hh = pad2(Math.min(Math.max(h, 0), 23));
  const mm = pad2(Math.min(Math.max(m, 0), 59));
  const ss = pad2(Math.min(Math.max(s, 0), 59));
  return `${hh}:${mm}:${ss}`;
};
const toSeconds = (t: string): number => {
  const [hh, mm, ss] = normalizeTime(t).split(':').map(Number);
  return hh * 3600 + mm * 60 + ss;
};
const overlaps = (aS: string, aE: string, bS: string, bE: string): boolean => {
  const as = toSeconds(aS), ae = toSeconds(aE);
  const bs = toSeconds(bS), be = toSeconds(bE);
  return as < be && bs < ae;
};

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [forums, setForums] = useState<Forum[]>([]);
  const [commonAreas, setCommonAreas] = useState<CommonArea[]>([]);
  const [totalCommonAreasCount, setTotalCommonAreasCount] = useState<number>(0);
  const [supportRequests, setSupportRequests] = useState<SupportRequest[]>([]);
  const [dashboardAlerts, setDashboardAlerts] = useState<Alert[]>([]);
  const [dataLoadStates, setDataLoadStates] = useState<DataLoadStates>(() => createInitialDataLoadStates());
  const { toast } = useToast();
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const hasBootstrappedRef = useRef(false);

  const setDataLoadState = useCallback((key: keyof DataLoadStates, status: DataLoadStatus) => {
    setDataLoadStates(prev => ({ ...prev, [key]: status }));
  }, []);

  const showActionError = useCallback(
    (title: string, error: unknown, fallback: string) => {
      toast({
        title,
        description: getErrorMessage(error, fallback),
        variant: 'destructive',
      });
    },
    [toast],
  );

  const fetchCommunities = useCallback(async (): Promise<Community[]> => {
    return fetchCommunitiesForUser(currentUser);
  }, [currentUser]);

  const reloadCommunities = useCallback(async () => {
    setDataLoadState('communities', 'loading');
    try {
      const sorted = await fetchCommunities();
      setCommunities(sorted);
      setDataLoadState('communities', 'ready');
      return sorted;
    } catch (error) {
      setCommunities([]);
      setDataLoadState('communities', 'error');
      throw error;
    }
  }, [fetchCommunities, setDataLoadState]);

  // Inicializar datos reales compartidos. Si una carga falla, los KPIs reciben estado de error.
  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (currentUser?.status === 'pending_age_declaration') {
      setCommunities([]);
      setPayments([]);
      setIncidents([]);
      setCommonAreas([]);
      setDataLoadState('communities', 'idle');
      setDataLoadState('payments', 'idle');
      setDataLoadState('incidents', 'idle');
      setDataLoadState('commonAreas', 'idle');
      return;
    }

    let isMounted = true;

    const bootstrap = async () => {
      let sortedCommunities: Community[] = [];
      let communitiesLoadFailed = false;

      setDataLoadState('communities', 'loading');
      try {
        const sorted = await fetchCommunities();
        sortedCommunities = sorted;
        if (isMounted) {
          setCommunities(sorted);
          setDataLoadState('communities', 'ready');
        }
      } catch (_error) {
        communitiesLoadFailed = true;
        if (isMounted) {
          // Si falla la carga, deja communities vacío (UI mostrará sin comunidades)
          setCommunities([]);
          setDataLoadState('communities', 'error');
        }
      }
      if (!isMounted) return;

      setDataLoadState('payments', 'loading');
      if (communitiesLoadFailed) {
        setPayments([]);
        setDataLoadState('payments', 'error');
      } else {
        try {
          const loadedPayments = await fetchPaymentsForCommunities(
            currentUser,
            sortedCommunities,
          );
          if (isMounted) {
            setPayments(loadedPayments);
            setDataLoadState('payments', 'ready');
          }
        } catch (error) {
          console.warn('No se pudieron cargar los pagos de comunidad:', error);
          if (isMounted) {
            setPayments([]);
            setDataLoadState('payments', 'error');
          }
        }
      }

      if (!hasBootstrappedRef.current) {
        // Las colecciones no migradas empiezan vacias y no alimentan KPIs.
        setPolls([]);
        setReservations([]);
        setMessages([]);
        setForums([]);
        hasBootstrappedRef.current = true;
      }
    };

    void bootstrap();

    return () => {
      isMounted = false;
    };
  }, [currentUser, fetchCommunities, isAuthLoading, setDataLoadState]);

  // Cargar áreas comunes
  useEffect(() => {
    if (currentUser?.status === 'pending_age_declaration') {
      setCommonAreas([]);
      setTotalCommonAreasCount(0);
      setDataLoadState('commonAreas', 'idle');
      return;
    }

    const fetchAreas = async () => {
      setDataLoadState('commonAreas', 'loading');
      try {
        const { areas, totalCount } = await fetchCommonAreasForUser(currentUser);
        setCommonAreas(areas);
        setTotalCommonAreasCount(totalCount);
        setDataLoadState('commonAreas', 'ready');
      } catch (error) {
        console.error('No se pudieron cargar las áreas comunes', error);
        setCommonAreas([]);
        setTotalCommonAreasCount(0);
        setDataLoadState('commonAreas', 'error');
      }
    };
    fetchAreas();
  }, [currentUser, setDataLoadState]);

  // Cargar comunidad real del usuario si no existe en el array actual
  useEffect(() => {
    if (currentUser?.status === 'pending_age_declaration') {
      return;
    }

    const ensureCommunity = async () => {
      try {
        const communityToAdd = await fetchMissingPrimaryCommunity(currentUser, communities);
        if (communityToAdd) {
          setCommunities(prev => sortCommunitiesByName(replaceById(prev, communityToAdd)));
        }
      } catch (err) {
        // Silencioso: si falla no bloquea UI
      }
    };
    ensureCommunity();
  }, [currentUser, communities]);

  // Cargar incidencias desde Supabase según la comunidad del usuario
  useEffect(() => {
    if (currentUser?.status === 'pending_age_declaration') {
      setIncidents([]);
      setDataLoadState('incidents', 'idle');
      return;
    }

    const fetchIncidents = async () => {
      setDataLoadState('incidents', 'loading');
      try {
        const loadedIncidents = await fetchIncidentsForUser(currentUser);
        setIncidents(loadedIncidents);
        setDataLoadState('incidents', 'ready');
      } catch (error: unknown) {
        console.error('Error al cargar incidencias:', error);
        setIncidents([]);
        setDataLoadState('incidents', 'error');
      }
    };
    fetchIncidents();
  }, [currentUser, setDataLoadState, toast]);

  // Community
  const getCommunity = (id: string) => selectCommunity(communities, id);
  const getCommunityMembers = (communityId: string) =>
    selectCommunityMembers(communities, communityId);

  // Incidents
  const getIncident = (id: string) => selectIncident(incidents, id);
  const getCommunityIncidents = (communityId: string) =>
    selectCommunityIncidents(incidents, communityId);
  const createIncident = async (incident: Omit<Incident, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const created = await createIncidentRecord({
        title: incident.title,
        description: incident.description,
        communityId: incident.communityId,
        createdBy: incident.reportedById,
        status: incident.status,
      });
      setIncidents(prev => prependUniqueById(prev, created));
      toast({
        title: 'Incidencia creada',
        description: 'La incidencia se ha registrado correctamente.',
      });
      return created;
    } catch (error: unknown) {
      showActionError('No se pudo crear la incidencia', error, 'Ocurrió un error al guardar la incidencia.');
      return null;
    }
  };
  const updateIncidentStatus = async (id: string, status: IncidentStatus) => {
    try {
      const updated = await updateIncidentStatusRecord(id, status);
      setIncidents(prev => replaceById(prev, updated));
      toast({
        title: 'Estado actualizado',
        description: 'El estado de la incidencia se ha actualizado correctamente.',
      });
      return updated;
    } catch (error: unknown) {
      showActionError('No se pudo actualizar la incidencia', error, 'Ocurrió un error al actualizar el estado.');
      return null;
    }
  };

  const addIncidentComment = async (id: string, comment: IncidentComment) => {
    try {
      const updated = await addIncidentCommentRecord(id, comment);
      setIncidents(prev => replaceById(prev, updated));
      toast({
        title: 'Comentario guardado',
        description: 'El comentario se ha añadido correctamente.',
      });
      return updated;
    } catch (error: unknown) {
      showActionError('No se pudo guardar el comentario', error, 'Ocurrió un error al guardar el comentario.');
      return null;
    }
  };

  const removeIncidentComment = async (id: string, index: number) => {
    try {
      const updated = await removeIncidentCommentRecord(id, index);
      setIncidents(prev => replaceById(prev, updated));
      toast({
        title: 'Comentario eliminado',
        description: 'El comentario se ha eliminado correctamente.',
      });
      return updated;
    } catch (error: unknown) {
      showActionError('No se pudo eliminar el comentario', error, 'Ocurrió un error al eliminar el comentario.');
      return null;
    }
  };

  const updateIncident = async (
    id: string,
    updates: { title: string; description: string; communityId: string; status: IncidentStatus }
  ) => {
    try {
      const updated = await updateIncidentRecord(id, updates);
      setIncidents(prev => replaceById(prev, updated));
      toast({
        title: 'Incidencia actualizada',
        description: 'Los detalles de la incidencia se han actualizado correctamente.',
      });
      return updated;
    } catch (error: unknown) {
      showActionError(
        'No se pudo actualizar la incidencia',
        error,
        'Ocurrió un error al actualizar la incidencia seleccionada.',
      );
      return null;
    }
  };
  const deleteIncident = async (id: string) => {
    const targetIncident = getIncident(id);
    const deletionValidation = validateIncidentDeletion(currentUser, targetIncident);
    if (!deletionValidation.canDelete) {
      toast({
        title: deletionValidation.title,
        description: deletionValidation.description,
        variant: 'destructive',
      });
      return false;
    }

    try {
      if (deletionValidation.isAppAdmin) {
        await forceDeleteIncidentRecord(id, targetIncident?.communityId);
      } else {
        await deleteIncidentRecord(id, currentUser!.id, targetIncident?.communityId);
      }
      setIncidents(prev => removeById(prev, id));
      toast({
        title: 'Incidencia eliminada',
        description: 'La incidencia se ha eliminado correctamente.',
      });
      return true;
    } catch (error: unknown) {
      showActionError('No se pudo eliminar la incidencia', error, 'Ocurrió un error al eliminar la incidencia.');
      return false;
    }
  };

  // Payments
  const getPayment = (id: string) => selectPayment(payments, id);
  const getCommunityPayments = (communityId: string) =>
    selectCommunityPayments(payments, communityId);
  const getUserPayments = (userId: string) =>
    selectUserPayments(payments, userId);
  const markPaymentAsPaid = async (id: string): Promise<boolean> => {
    const payment = getPayment(id);

    if (!payment) {
      toast({
        title: "Pago no encontrado",
        description: "No se ha localizado el pago que intentas actualizar.",
        variant: 'destructive',
      });
      return false;
    }

    if (isCompletedPayment(payment)) {
      toast({
        title: "Pago ya registrado",
        description: "Este pago ya figura como completado.",
      });
      return true;
    }

    try {
      const updatedPayment = await updateCommunityPaymentRecord(id, {
        status: 'paid',
        paidDate: resolvePaidDate(payment),
      });

      setPayments(prev => replaceById(prev, updatedPayment));
      toast({ title: "Pago registrado", description: "El pago se ha marcado como completado y se ha guardado en base de datos." });
      return true;
    } catch (error: unknown) {
      toast({
        title: "No se pudo registrar el pago",
        description: error instanceof Error ? error.message : "Ocurrió un error al actualizar el pago en base de datos.",
        variant: 'destructive',
      });
      return false;
    }
  };
  const createPayment = async (payment: Omit<Payment, 'id'> & { id?: string }) => {
    const paymentId = payment.id || generateId();
    const normalizedAttachments = normalizePaymentAttachments(payment, paymentId);

    try {
      const createdPayment = await createCommunityPaymentRecord({
        id: payment.id,
        communityId: payment.communityId,
        userId: payment.userId,
        description: payment.description,
        amount: payment.amount,
        dueDate: payment.dueDate,
        paidDate: payment.paidDate,
        type: payment.type,
        status: payment.status ?? 'pending_collection',
        attachments: normalizedAttachments,
      });

      setPayments(prev => [...prev, { ...createdPayment, attachments: normalizedAttachments }]);
      toast({ title: "Nuevo pago creado", description: "Se ha registrado un nuevo pago en el sistema" });
      return createdPayment;
    } catch (error: unknown) {
      toast({
        title: "No se pudo crear el pago",
        description: error instanceof Error ? error.message : "Ocurrió un error al guardar el pago en base de datos",
        variant: 'destructive',
      });
      return null;
    }
  };
  const updatePayment = async (id: string, updates: Partial<Omit<Payment, 'id' | 'communityId'>>): Promise<boolean> => {
    try {
      const updatedPayment = await updateCommunityPaymentRecord(id, toPaymentUpdateInput(updates));
      setPayments(prev => replaceById(prev, updatedPayment));
      toast({ title: "Pago actualizado", description: "Los cambios del pago se han guardado correctamente" });
      return true;
    } catch (error: unknown) {
      toast({
        title: "No se pudo actualizar el pago",
        description: error instanceof Error ? error.message : "Ocurrió un error al guardar los cambios del pago",
        variant: 'destructive',
      });
      return false;
    }
  };
  const deletePayment = async (id: string): Promise<boolean> => {
    if (!canDeletePayment(currentUser)) {
      toast({
        title: "Acción no permitida",
        description: "Solo el administrador de la comunidad puede eliminar pagos.",
        variant: 'destructive',
      });
      return false;
    }

    try {
      await deleteCommunityPaymentRecord(id);
      setPayments(prev => removeById(prev, id));
      toast({ title: "Pago eliminado", description: "El registro de pago se ha eliminado correctamente" });
      return true;
    } catch (error: unknown) {
      toast({
        title: "No se pudo eliminar el pago",
        description: error instanceof Error ? error.message : "Ocurrió un error al eliminar el pago",
        variant: 'destructive',
      });
      return false;
    }
  };
  const markPaymentsAsPaidBulk = (ids: string[]) => {
    setPayments(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: 'paid' } : p));
    toast({ title: "Pagos registrados", description: `Se han registrado ${ids.length} pagos como completados` });
  };
  const sendPaymentReminders = ({ communityId, minOverdueDays = 0 }: { communityId?: string; minOverdueDays?: number }) => {
    const overduePayments = filterOverduePayments(payments, { communityId, minOverdueDays });
    toast({ title: "Recordatorios enviados", description: `Se han enviado ${overduePayments.length} recordatorios de pago` });
    return overduePayments.length;
  };
  const exportPaymentsCSV = ({ communityId }: { communityId?: string }) => {
    const targetPayments = communityId ? getCommunityPayments(communityId) : payments;
    const csv = buildPaymentsCsv(targetPayments);
    toast({ title: "Exportación completa", description: `Se exportaron ${targetPayments.length} pagos a CSV` });
    return csv;
  };
  const downloadPaymentReceipts = ({ userId, communityId }: { userId?: string; communityId?: string }) => {
    const filtered = filterPaymentsForReceipts(payments, { userId, communityId });
    toast({ title: "Recibos descargados", description: `Se descargaron ${filtered.length} recibos en PDF` });
  };
  const downloadPaymentReceipt = (paymentId: string) => {
    const payment = getPayment(paymentId);
    if (!payment) {
      toast({ title: "Error", description: "No se encontró el pago especificado" });
      return;
    }
    toast({ title: "Recibo descargado", description: `Se descargó el recibo del pago ${payment.id} en PDF` });
  };
  const createLevy = ({ communityId, amount, dueDate, description }: { communityId: string; amount: number; dueDate: string; description?: string }) => {
    const community = getCommunity(communityId);
    if (!community) {
      toast({ title: "Error", description: "No se encontró la comunidad especificada" });
      return 0;
    }
    const newPayments = buildLevyPayments(
      community,
      { communityId, amount, dueDate, description },
      generateId,
    );
    setPayments(prev => [...prev, ...newPayments]);
    toast({ title: "Cuota creada", description: `Se crearon ${newPayments.length} pagos para los miembros de la comunidad` });
    return newPayments.length;
  };
  const executePaymentSearchAction = (action: PaymentSearchAction, filters?: PaymentActionFilters) => {
    switch (action.type) {
      case 'remind': sendPaymentReminders({ communityId: filters?.communityId }); break;
      case 'markPaid':
        if (hasStringArrayField(action.payload, 'ids')) {
          markPaymentsAsPaidBulk(action.payload.ids);
        }
        break;
      case 'export': exportPaymentsCSV({ communityId: filters?.communityId }); break;
      case 'createLevy':
        if (isCreateLevyActionPayload(action.payload)) {
          createLevy(action.payload);
        }
        break;
      case 'downloadReceipts': downloadPaymentReceipts(filters || {}); break;
    }
  };

  // Polls
  const getPoll = (id: string) => selectPoll(polls, id);
  const getCommunityPolls = (communityId: string) => selectCommunityPolls(polls, communityId);
  const createPoll = (poll: Omit<Poll, 'id'>) => {
    setPolls(prev => [...prev, createPollRecord(poll, generateId)]);
  };
  const vote = (pollId: string, optionId: string, userId: string) => {
    setPolls(prev => addPollVote(prev, { pollId, optionId, userId }));
    return true;
  };
  const closePoll = (id: string) => {
    setPolls(prev => closePollById(prev, id));
  };
  const hasUserVoted = (pollId: string, userId: string) => {
    return hasUserVotedInPoll(polls, pollId, userId);
  };

  // Reservations
  const getReservation = (id: string) => selectReservation(reservations, id);
  const getUserReservations = (userId: string) => selectUserReservations(reservations, userId);
  const getAreaReservations = (areaId: string) => selectAreaReservations(reservations, areaId);
  const getDayReservations = (areaId: string, date: string) =>
    selectDayReservations(reservations, areaId, date);
  const createReservation = (reservation: Omit<Reservation, 'id' | 'createdAt' | 'status'>) => {
    const dayReservations = getDayReservations(reservation.commonAreaId, reservation.date);
    const conflict = findReservationConflict(dayReservations, reservation, normalizeTime, overlaps);
    if (conflict) {
      toast({ title: "Error", description: "El horario seleccionado se superpone con otra reserva" });
      return false;
    }
    const newReservation = createReservationRecord(reservation, {
      generateId,
      getCurrentDate,
      normalizeTime,
    });
    setReservations(prev => [...prev, normalizeReservation(newReservation)]);
    toast({ title: "Reserva creada", description: "La reserva se ha creado exitosamente" });
    return true;
  };
  const cancelReservation = (id: string) => {
    setReservations(prev => cancelReservationById(prev, id));
    toast({ title: "Reserva cancelada", description: "La reserva ha sido cancelada" });
  };
  const canUserReserve = (userId: string, areaId: string, date: string) => {
    const area = selectCommonArea(commonAreas, areaId);
    return canUserReserveArea(reservations, area, { userId, areaId, date });
  };

  // Common Areas
  const updateCommonArea = (id: string, updates: Partial<CommonArea>) => {
    setCommonAreas(prev => upsertCommonArea(prev, id, updates));
  };

  const removeCommonArea = (id: string, communityId?: string) => {
    setCommonAreas(prev => removeCommonAreaByScope(prev, id, communityId));
  };

  const updateCommunityHousingConfig = async (
    communityId: string,
    config: Partial<Pick<Community, 'blocks' | 'floors' | 'letters'>>
  ) => {
    try {
      const updated = await updateCommunityHousingConfigApi(communityId, config);
      if (!updated) {
        throw new Error('No se pudo guardar la configuración de viviendas.');
      }
      setCommunities(prev =>
        applyCommunityHousingConfig(prev, communityId, updated)
      );
      toast({
        title: 'Configuración guardada',
        description: 'La configuración de viviendas se ha actualizado correctamente.',
      });
      return true;
    } catch (error: unknown) {
      toast({
        title: 'No se pudo guardar la configuración',
        description: error instanceof Error
          ? error.message
          : 'Ocurrió un error al actualizar la configuración de la comunidad.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Messages
  const getCommunityMessages = (communityId: string) => selectCommunityMessages(messages, communityId);
  const getForumMessages = (forumId: string) => selectForumMessages(messages, forumId);
  const getUserPrivateMessages = (userId: string) => selectUserPrivateMessages(messages, userId);
  const getConversation = (user1Id: string, user2Id: string) =>
    selectConversation(messages, user1Id, user2Id);
  const sendMessage = (message: Omit<Message, 'id' | 'createdAt' | 'isRead'>) => {
    const newMessage = createMessageRecord(message, { generateId, getCurrentDate });
    setMessages(prev => [...prev, newMessage]);
  };

  // Forums
  const getCommunityForums = (communityId: string) => selectCommunityForums(forums, communityId);
  const createForum = (forum: Omit<Forum, 'id' | 'createdAt'>) => {
    setForums(prev => [...prev, createForumRecord(forum, { generateId, getCurrentDate })]);
  };

  const addSupportRequest = useCallback((req: SupportRequest) => {
    setSupportRequests(prev => [req, ...prev]);
    setDashboardAlerts(prev => [buildSupportRequestAlert(req), ...prev]);
    console.info('[Soporte] Nueva solicitud', req);
    // TODO: integrar backend/email/push.
  }, []);

  const communityDataValue = React.useMemo<CommunityDataContextType>(
    () => ({
      communities,
      reloadCommunities,
    }),
    [communities, reloadCommunities],
  );

  return (
    <CommunityDataContext.Provider value={communityDataValue}>
      <DataContext.Provider value={{
        communities, incidents, payments, polls, reservations, messages, forums, commonAreas,
        totalCommonAreasCount,
        supportRequests, dashboardAlerts,
        dataLoadStates,
        reloadCommunities,
        getCommunity, getCommunityMembers,
        getIncident, getCommunityIncidents, createIncident, updateIncidentStatus, updateIncident, deleteIncident,
        addIncidentComment,
        removeIncidentComment,
        getPayment, getCommunityPayments, getUserPayments, markPaymentAsPaid, createPayment, deletePayment, updatePayment, markPaymentsAsPaidBulk, sendPaymentReminders, exportPaymentsCSV, downloadPaymentReceipts, downloadPaymentReceipt, createLevy, executePaymentSearchAction,
        getPoll, getCommunityPolls, createPoll, vote, closePoll, hasUserVoted,
        getReservation, getUserReservations, getAreaReservations, getDayReservations, createReservation, cancelReservation, canUserReserve,
        updateCommonArea,
        removeCommonArea,
        updateCommunityHousingConfig,
        getCommunityMessages, getForumMessages, getUserPrivateMessages, getConversation, sendMessage,
        getCommunityForums, createForum,
        addSupportRequest,
      }}>
        {children}
      </DataContext.Provider>
    </CommunityDataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error("useData must be used within a DataProvider");
  return context;
};

export const useCommunityData = () => {
  const context = useContext(CommunityDataContext);
  if (!context) throw new Error("useCommunityData must be used within a DataProvider");
  return context;
};
